import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../auth/AuthContext';
import { BrandLogo } from '../components/BrandLogo';
import { PrimaryButton } from '../components/premium';
import { appPath } from '../lib/appPaths';
import { isNativeApp, isNativeIOS } from '../lib/platform';
import { ApiError } from '../lib/api';
import { webSignedInHome } from '../lib/webAccountPaths';

export function LoginPage() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, loginWithApple } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      const u = await login(email, password);
      if (!isNativeApp()) {
        const next = searchParams.get('next');
        nav(next && next.startsWith('/') ? next : webSignedInHome(u), { replace: true });
        return;
      }
      const next = searchParams.get('next');
      nav(next && next.startsWith('/') ? next : appPath('/'), { replace: true });
    } catch (ex: unknown) {
      if (ex instanceof ApiError) {
        try {
          const j = JSON.parse(ex.body ?? '{}') as { error?: string };
          setErr(j.error ?? 'Could not sign in.');
        } catch {
          setErr('Could not sign in.');
        }
      } else if (
        ex instanceof TypeError ||
        (ex instanceof Error && /fetch|Failed to fetch|ECONNREFUSED|ECONNRESET|Load failed/i.test(ex.message))
      ) {
        setErr('Unable to reach the server. Check your connection and try again.');
      } else {
        setErr('Could not sign in.');
      }
    } finally {
      setBusy(false);
    }
  }

  async function onAppleSignIn() {
    setErr(null);
    setBusy(true);
    try {
      const u = await loginWithApple();
      if (!isNativeApp()) {
        nav(webSignedInHome(u), { replace: true });
        return;
      }
      nav(appPath('/'), { replace: true });
    } catch (ex: unknown) {
      setErr(ex instanceof Error ? ex.message : 'Apple sign-in failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="app-body relative flex min-h-screen flex-col items-center justify-center px-4 pb-[env(safe-area-inset-bottom)] pt-[max(2rem,env(safe-area-inset-top))]">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <div className="mb-6 flex justify-center">
          <BrandLogo variant="hero" />
        </div>
        <h1 className="text-center font-display text-2xl font-bold text-[var(--text-primary)]">Sign in</h1>
        <p className="mt-2 text-center text-sm text-[var(--text-muted)]">
          Manage billing, or continue setup with the same account you use in the app.
        </p>
        <form onSubmit={onSubmit} className="premium-card mt-8 space-y-4 !p-6">
          <label className="block text-xs font-medium text-[var(--text-muted)]">
            Email
            <input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1.5 w-full rounded-xl border border-white/10 bg-black/30 px-3 py-2.5 text-sm text-white"
            />
          </label>
          <label className="block text-xs font-medium text-[var(--text-muted)]">
            Password
            <input
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1.5 w-full rounded-xl border border-white/10 bg-black/30 px-3 py-2.5 text-sm text-white"
            />
          </label>
          {err ? <p className="text-sm text-rose-300">{err}</p> : null}
          <PrimaryButton type="submit" disabled={busy} className="w-full">
            {busy ? 'Signing in…' : 'Sign in'}
          </PrimaryButton>
          {isNativeIOS() ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => void onAppleSignIn()}
              className="w-full rounded-xl border border-white/15 px-4 py-3 text-sm font-semibold text-white"
            >
              Sign in with Apple
            </button>
          ) : null}
        </form>
        <p className="mt-6 text-center text-sm text-[var(--text-muted)]">
          New here?{' '}
          <Link
            to={searchParams.get('next') ? `/signup?next=${encodeURIComponent(searchParams.get('next')!)}` : '/signup'}
            className="font-semibold text-accent"
          >
            Create an account
          </Link>
        </p>
      </motion.div>
    </div>
  );
}
